import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";
import { saveRole, updateRole } from "../../features/admin/RoleSlice";
import { toast } from "react-toastify";
import { convertToSlug } from "../../utils/helps";

const deprecatedPermissions = ["CHANGE_FEE_USER"];

const roleSchema = Yup.object().shape({
  roleName: Yup.string()
    .trim()
    .min(3, "Tên chức vụ phải ít nhất 3 ký tự")
    .required("Tên chức vụ không được để trống"),
  permissions: Yup.array().min(1, "Chọn ít nhất một quyền"),
});

export default function RoleModal({ role, isOpen, onClose }) {
  const dispatch = useDispatch();
  const { status: saveStatus, error } = useSelector((state) => state.roles);
  const permissions = useSelector((state) => state.roles?.permissions);
  const [roleName, setRoleName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (role) {
      setRoleName(role.roleName || "");
      setSelectedPermissions(role?.permissions?.map((p) => p.id) || []);
    } else {
      setRoleName("");
      setSelectedPermissions([]);
    }
  }, [role]);
  let groupedPermissionsTypeSystem = permissions
    ?.filter((perm) => perm.type === "System")
    ?.reduce((acc, perm) => {
      if (perm.parentId === null) {
        acc[perm.id] = { ...perm, children: [] };
      } else {
        acc[perm.parentId]?.children?.push(perm);
      }
      return acc;
    }, {});
  const groupedPermissionsUser = permissions
    ?.filter((perm) => perm.type === "User")
    ?.reduce((acc, perm) => {
      if (perm.parentId === null) {
        acc[perm.id] = { ...perm, children: [] };
      } else {
        acc[perm.parentId]?.children?.push(perm);
      }
      return acc;
    }, {});

  const togglePermission = (id, children = []) => {
    setErrors((prev) => ({ ...prev, permissions: "" }));
    setSelectedPermissions((prev) =>
      prev.includes(id)
        ? prev.filter((perm) => perm !== id && !children?.includes(perm))
        : [...prev, id, ...children.filter((child) => !prev.includes(child))]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = { roleName, permissions: selectedPermissions };
    let data = {
      roleName: formData.roleName,
      roleSlug: convertToSlug(formData.roleName),
      permissionIds: formData.permissions,
    };

    try {
      await roleSchema.validate(formData, { abortEarly: false });

      if (role?.id) {
        let dataUpdate = {
          ...data,
          id: role.id,
        };

        await dispatch(updateRole(dataUpdate)).unwrap();
        toast.success("Cập nhật chức vụ thành công!");
      } else {
        await dispatch(saveRole(data)).unwrap();
        toast.success("Tạo chức vụ thành công!");
      }

      onClose();
    } catch (error) {
      if (error instanceof Yup.ValidationError) {
        const formErrors = {};
        error.inner.forEach((err) => {
          formErrors[err.path] = err.message;
        });
        setErrors(formErrors);
      } else {
        if (error.messages.roleName === "Dữ liệu đã tồn tại") {
          toast.error("Chức vụ đã tồn tại");
        } else {
          toast.error("Có lỗi xảy ra");
        }
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-base-100 rounded-md mx-6 w-full lg:w-2/3 h-3/4 h-max-[3/4] overflow-y-auto">
        <div className="flex p-6 max-h-20 sticky top-0 bg-base-100 z-10 pb-4 justify-between items-center">
          <h2 className="text-xl font-semibold mb-4">
            {role?.id ? "Chỉnh sửa chức vụ" : "Tạo chức vụ"}
          </h2>
          <button
            onClick={onClose}
            className="px-4 py-2 border rounded-md shadow-sm text-sm font-medium text-base-700 bg-base-100 hover:bg-base-200"
          >
            X
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 pb-4 sticky top-20 bg-base-100 z-10">
            <label htmlFor="roleName" className="block text-sm font-medium">
              Tên chức vụ<i className="text-red-600"> *</i>
            </label>
            <input
              type="text"
              placeholder="Nhập tên chức vụ"
              value={roleName}
              onChange={(e) => {
                setErrors((prev) => ({ ...prev, roleName: "" }));
                setRoleName(e.target.value);
              }}
              className="input input-bordered w-full"
            />
            {errors.roleName && (
              <p className="text-red-500 text-sm my-2">{errors.roleName}</p>
            )}
          </div>
          <div className="mb-4 px-6">
            <h3 className="text-lg font-medium mb-4">Phân quyền hệ thống</h3>
            <div className="grid grid-cols-4 gap-4 mb-2">
              {Object.values(groupedPermissionsTypeSystem).map((group) => (
                <div key={group.id} className="pb-4 mb-4 rounded-lg">
                  <label className="flex items-center gap-2 font-semibold">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-info"
                      checked={
                        group.children?.length > 0
                          ? group.children.every((perm) =>
                              selectedPermissions.includes(perm.id)
                            )
                          : selectedPermissions.includes(group.id)
                      }
                      onChange={() =>
                        group.children?.length > 0
                          ? togglePermission(
                              group.id,
                              group.children
                                .sort((a, b) =>
                                  a.permissionName.localeCompare(
                                    b.permissionName
                                  )
                                )
                                .map((perm) => perm.id)
                            )
                          : togglePermission(group.id)
                      }
                    />
                    {group.permissionName}
                  </label>
                  <div className="grid grid-cols-1 gap-2 mt-2 ml-6">
                    {group.children
                      .sort((a, b) =>
                        a.permissionName.localeCompare(b.permissionName)
                      )
                      .map((perm) => (
                        <>
                          {!deprecatedPermissions.includes(
                            perm.permissionSlug
                          ) && (
                            <label
                              key={perm.id}
                              className="flex items-center gap-2"
                            >
                              <input
                                type="checkbox"
                                className="checkbox checkbox-info"
                                checked={selectedPermissions.includes(perm.id)}
                                onChange={() => togglePermission(perm.id)}
                              />
                              {perm.permissionName}
                            </label>
                          )}
                        </>
                      ))}
                  </div>
                </div>
              ))}
            </div>
            {errors.permissions && (
              <p className="text-red-500 text-sm my-2">{errors.permissions}</p>
            )}
            <hr />
            <h3 className="text-lg font-medium mb-4 pt-2">
              Phân quyền bài viết
            </h3>
            <div className="grid grid-cols-2 gap-4">
              {Object.values(groupedPermissionsUser).map((group) => (
                <div key={group.id} className="pb-2 rounded-lg">
                  <label className="flex items-center gap-2 font-semibold">
                    <input
                      type="checkbox"
                      className="checkbox checkbox-info"
                      checked={
                        group.children?.length > 0
                          ? group.children.every((perm) =>
                              selectedPermissions.includes(perm.id)
                            )
                          : selectedPermissions.includes(group.id)
                      }
                      onChange={() =>
                        group.children?.length > 0
                          ? togglePermission(
                              group.id,
                              group.children.map((perm) => perm.id)
                            )
                          : togglePermission(group.id)
                      }
                    />
                    {group.permissionName}
                  </label>
                  <div className="grid grid-cols-2 gap-2 mt-2 ml-4">
                    {group.children.map((perm) => (
                      <label key={perm.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-info"
                          checked={selectedPermissions.includes(perm.id)}
                          onChange={() => togglePermission(perm.id)}
                        />
                        {perm.permissionName}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            {errors.permissions && (
              <p className="text-red-500 text-sm my-2">{errors.permissions}</p>
            )}
          </div>

          <div className="flex justify-end space-x-4 p-6 border-t sticky bottom-0 bg-base-100 z-10 pt-4">
            <button type="button" className="btn btn-sm" onClick={onClose}>
              Hủy
            </button>
            <button
              type="submit"
              className="btn btn-primary btn-sm"
              disabled={saveStatus === "loading"}
            >
              {saveStatus === "loading"
                ? "Đang lưu..."
                : role?.id
                ? "Cập nhật"
                : "Tạo"}
            </button>
          </div>
          {error && (
            <p className="text-red-500 text-sm mt-2">
              Đã xảy ra lỗi: {error.message}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}
